import React, { useEffect, useRef, useState } from "react";
import { TL } from "../i18n";
import { useApp } from "../context/AppContext";
import client from "../lib/api";
import { Modal, Button } from "./ui";

const IDLE_MS = 15 * 60 * 1000;   // sign out after 15 minutes of inactivity
const WARN_MS = 60 * 1000;        // show a 1-minute countdown before that

// Auto signs-out inactive Runner sessions (role "user") and shows a 1-minute countdown clock.
export default function IdleGuard() {
  const { user, logout } = useApp();
  const active = user?.role === "user";
  const lastRef = useRef(Date.now());
  const [warn, setWarn] = useState(false);
  const [left, setLeft] = useState(60);

  useEffect(() => {
    if (!active) return;
    const bump = () => { if (!warn) lastRef.current = Date.now(); };
    const evts = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const signOut = async () => {
      try { await client.post("/auth/end-session"); } catch (e) { /* revoke best-effort */ }
      logout();
    };

    const tick = setInterval(() => {
      const idle = Date.now() - lastRef.current;
      if (idle >= IDLE_MS) { clearInterval(tick); signOut(); return; }
      if (idle >= IDLE_MS - WARN_MS) {
        setWarn(true);
        setLeft(Math.max(0, Math.ceil((IDLE_MS - idle) / 1000)));
      } else if (warn) {
        setWarn(false);
      }
    }, 1000);

    return () => { evts.forEach((e) => window.removeEventListener(e, bump)); clearInterval(tick); };
  }, [active, warn, logout]);

  // Nudge: flash the browser tab title while the countdown is showing.
  useEffect(() => {
    if (!warn) return;
    const original = document.title;
    let on = false;
    const flip = () => { document.title = on ? original : "\u26A0 Signing out\u2026"; on = !on; };
    flip();
    const id = setInterval(flip, 900);
    return () => { clearInterval(id); document.title = original; };
  }, [warn]);

  // Nudge: subtle beep at warning start, then a soft tick during the final 10 seconds.
  useEffect(() => {
    if (!warn) return;
    if (left > 10 && left !== 60) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = left <= 10 ? 880 : 660;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
      osc.onended = () => ctx.close().catch(() => {});
    } catch (e) { /* audio best-effort */ }
  }, [warn, left]);

  const stay = () => { lastRef.current = Date.now(); setWarn(false); };
  if (!active) return null;

  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <Modal open={warn} onClose={stay} title={TL("Still there?")} testid="idle-timeout-modal">
      <div className="space-y-4 text-sm">
        <p>{TL("For your security, you'll be signed out due to inactivity in:")}</p>
        <div className="text-center font-head text-4xl tabular-nums text-[#DA1E28]" data-testid="idle-countdown">{mm}:{ss}</div>
        <div className="flex gap-2">
          <Button onClick={stay} className="flex-1" data-testid="idle-stay-btn">{TL("Stay signed in")}</Button>
          <Button variant="ghost" onClick={logout} data-testid="idle-signout-now-btn">{TL("Sign out now")}</Button>
        </div>
      </div>
    </Modal>
  );
}
